import React, { useMemo } from 'react'
import { getCurrentLocale, formatCurrency } from 'utils'
import { trackEvent } from 'utils/analytics'
import { formatSchedulePattern } from 'utils/scheduleUtils'
import CoursePreviewCard from '../shared/CoursePreviewCard'

/**
 * GroupClassList — displays group class series as cards on a team brand page.
 *
 * Usage:
 *   <GroupClassList
 *     groupClasses={groupClasses}
 *     colors={colors}
 *     translations={translations}
 *     teamSlug="my-team"
 *     currentUser={currentUser}
 *     onEnroll={(groupClass) => openEnrollModal(groupClass)}
 *   />
 */

const DEFAULT_TRANSLATIONS = {
  title: 'Group Classes',
  subtitle: 'Open group class sessions',
  enroll_button: 'Enroll',
  register_first: 'Register',
  full: 'Full',
  spots_left: '%{count} spots left',
  online: 'Online',
  in_person: 'In Person',
  login_to_enroll: 'Log in to enroll',
  total_sessions: '%{count} sessions',
  learn_more: 'Learn More',
  sessions_unit: 'sessions',
  hours_unit: 'hours',
  enrolled_unit: 'enrolled',
  trial_free: 'Free Trial',
  trial_paid: 'NT$%{price} trial',
  enroll_trial: 'Enroll Trial',
  enroll_direct: 'Enroll',
  threshold_progress: '%{current}/%{min} registered',
  min_students_hint: 'Minimum %{min} students',
  already_registered: 'Registered',
  pending_payment: 'Pending Payment',
  already_confirmed: 'Enrolled',
}

function interpolate(template, vars) {
  if (!template) return ''
  return Object.entries(vars).reduce(
    (str, [k, v]) => str.replace(`%{${k}}`, v),
    template
  )
}

function formatSpotsLeft(template, count) {
  return interpolate(template || '%{count} spots left', { count })
}

function formatTotalSessions(template, count) {
  return interpolate(template || '%{count} sessions', { count })
}

function formatSessionDates(sessions) {
  if (!sessions || sessions.length === 0) return null
  const locale = getCurrentLocale() || 'zh-TW'
  const fmt = new Intl.DateTimeFormat(locale, { month: 'numeric', day: 'numeric' })
  return sessions.map(s => fmt.format(new Date(s.start_time)))
}

function formatTrialDate(session) {
  if (!session) return null
  const locale = getCurrentLocale() || 'zh-TW'
  const fmt = new Intl.DateTimeFormat(locale, { month: 'numeric', day: 'numeric', weekday: 'short' })
  return fmt.format(new Date(session.start_time))
}

function LocationBadge({ locationType, translations }) {
  const t = { ...DEFAULT_TRANSLATIONS, ...translations }

  if (locationType === 'online') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">
        <span aria-hidden="true">🌐</span>
        {t.online}
      </span>
    )
  }

  if (locationType === 'in_person') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">
        <span aria-hidden="true">📍</span>
        {t.in_person}
      </span>
    )
  }

  return null
}

/**
 * TrialPriceBadge — shows "免費試聽" (green) or "NT$X 試聽" (amber)
 */
function TrialPriceBadge({ trialClass, t }) {
  const isFree = (trialClass.trial_price ?? trialClass.price ?? 0) === 0
  if (isFree) {
    return (
      <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-semibold">
        {t.trial_free || 'Free Trial'}
      </span>
    )
  }
  const price = trialClass.trial_price > 0 ? trialClass.trial_price : trialClass.price
  const label = interpolate(t.trial_paid || 'NT$%{price} trial', { price: Math.round(price).toLocaleString() })
  return (
    <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold">
      {label}
    </span>
  )
}

/**
 * EnrollButton — renders the correct button based on enrollment_status.
 * Task 6.3: registered → disabled "已登記"; pending_payment → "待付費" (clickable);
 *            confirmed → disabled "已報名".
 */
function EnrollButton({ groupClass, t, colors, isFull, onClick }) {
  const status = groupClass.enrollment_status

  if (status === 'registered') {
    return (
      <button
        disabled
        className="w-full py-2.5 rounded-xl text-sm font-semibold text-white cursor-not-allowed opacity-70"
        style={{ backgroundColor: '#9CA3AF' }}
        aria-disabled
      >
        {t.already_registered || 'Registered'}
      </button>
    )
  }

  if (status === 'pending_payment') {
    return (
      <button
        onClick={onClick}
        className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all cursor-pointer"
        style={{ backgroundColor: '#F59E0B' }}
        aria-label={`${t.pending_payment || 'Pending Payment'} — ${groupClass.title}`}
      >
        {t.pending_payment || 'Pending Payment'}
      </button>
    )
  }

  if (status === 'confirmed') {
    return (
      <button
        disabled
        className="w-full py-2.5 rounded-xl text-sm font-semibold text-white cursor-not-allowed opacity-70"
        style={{ backgroundColor: '#10B981' }}
        aria-disabled
      >
        {t.already_confirmed || 'Enrolled'}
      </button>
    )
  }

  // Default: enroll button
  const isRegisterFirst = groupClass.enrollment_mode === 'register_first'
  const label = isFull ? t.full : (isRegisterFirst ? (t.register_first || 'Register') : t.enroll_button)

  return (
    <button
      onClick={isFull ? undefined : onClick}
      disabled={isFull}
      className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
      style={{ backgroundColor: isFull ? '#9CA3AF' : (colors?.primary || '#3366FF') }}
      aria-disabled={isFull}
      aria-label={isFull ? t.full : `${label} — ${groupClass.title}`}
    >
      {label}
    </button>
  )
}

/**
 * TrialSection — shown inside a regular class card when trial_classes exist.
 * Shows nearest trial date + badges + enroll buttons.
 */
function TrialSection({ regularClass, trialClasses, colors, t, onEnroll }) {
  if (!trialClasses || trialClasses.length === 0) return null

  return (
    <div className="mt-3 border-t border-dashed border-gray-200 pt-3 space-y-3">
      {trialClasses.map((trial) => {
        const nearestSession = trial.sessions?.[0]
        const trialDate = formatTrialDate(nearestSession)
        const trialIsFull = (trial.remaining_capacity ?? 0) <= 0
        const trialPrice = trial.trial_price > 0 ? trial.trial_price : trial.price
        const trialIsFree = trialPrice === 0

        const trialStatus = trial.enrollment_status

        return (
          <div key={trial.id} className="rounded-lg bg-gray-50 px-3 py-2.5 space-y-2">
            {/* Trial header: badge + date */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <TrialPriceBadge trialClass={trial} t={t} />
              {trialDate && (
                <span className="text-xs text-gray-500">{trialDate}</span>
              )}
            </div>

            {/* Threshold progress for trial */}
            {trial.min_students > 0 && (
              <p className="text-xs text-gray-500">
                {interpolate(t.threshold_progress || '%{current}/%{min} registered', {
                  current: trial.enrolled_count ?? 0,
                  min: trial.min_students
                })}
              </p>
            )}

            {/* Trial enrollment button (with status awareness) */}
            {trialStatus === 'registered' ? (
              <button
                disabled
                className="w-full py-2 rounded-lg text-xs font-semibold text-white cursor-not-allowed opacity-70"
                style={{ backgroundColor: '#9CA3AF' }}
              >
                {t.already_registered || 'Registered'}
              </button>
            ) : trialStatus === 'pending_payment' ? (
              <button
                onClick={() => onEnroll(trial)}
                className="w-full py-2 rounded-lg text-xs font-semibold text-white transition-all cursor-pointer"
                style={{ backgroundColor: '#F59E0B' }}
              >
                {t.pending_payment || 'Pending Payment'}
              </button>
            ) : trialStatus === 'confirmed' ? (
              <button
                disabled
                className="w-full py-2 rounded-lg text-xs font-semibold text-white cursor-not-allowed opacity-70"
                style={{ backgroundColor: '#10B981' }}
              >
                {t.already_confirmed || 'Enrolled'}
              </button>
            ) : (
              <button
                onClick={trialIsFull ? undefined : () => onEnroll(trial)}
                disabled={trialIsFull}
                className="w-full py-2 rounded-lg text-xs font-semibold text-white transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: trialIsFull ? '#9CA3AF' : (trialIsFree ? '#10B981' : (colors?.primary || '#3366FF')) }}
                aria-disabled={trialIsFull}
              >
                {trialIsFull ? t.full : (t.enroll_trial || 'Enroll Trial')}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

function GroupClassCard({ groupClass, colors, translations, currentUser, onEnroll, onOpenDetail }) {
  const t = { ...DEFAULT_TRANSLATIONS, ...translations }
  const sessionDates = useMemo(() => formatSessionDates(groupClass.sessions), [groupClass.sessions])

  const metaParts = []
  if (groupClass.teacher_name) metaParts.push(groupClass.teacher_name)
  if (sessionDates?.length > 0) metaParts.push(`${sessionDates.length} ${t.sessions_unit || 'sessions'}`)
  metaParts.push(formatCurrency(groupClass.price, groupClass.currency || 'TWD'))

  return (
    <CoursePreviewCard
      name={groupClass.title}
      imageUrl={groupClass.image_url}
      videoThumbnailUrl={groupClass.video_thumbnail_url}
      description={groupClass.description}
      metaText={metaParts.join(' · ')}
      buttonLabel={t.learn_more || 'Learn More'}
      onClick={() => onOpenDetail?.(groupClass)}
      colors={colors}
      studentsCount={groupClass.enrolled_count || 0}
      lessonsCount={0}
      studentsLabel={t.enrolled_unit || 'enrolled'}
    />
  )
}

export default function GroupClassList({ groupClasses, colors, translations, teamSlug, currentUser, onEnroll, onOpenDetail }) {
  const t = { ...DEFAULT_TRANSLATIONS, ...translations }

  if (!groupClasses || groupClasses.length === 0) return null

  return (
    <section className="py-8 border-t border-gray-200" aria-labelledby="group-class-section-title">
      <div className="mb-6">
        <h2
          id="group-class-section-title"
          className="text-[22px] font-semibold"
          style={{ color: '#222222' }}
        >
          {t.title}
        </h2>
        <p className="text-sm mt-1" style={{ color: '#717171' }}>
          {t.subtitle}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {groupClasses.map((groupClass) => (
          <GroupClassCard
            key={groupClass.id}
            groupClass={groupClass}
            colors={colors}
            translations={t}
            currentUser={currentUser}
            onEnroll={onEnroll}
            onOpenDetail={onOpenDetail}
          />
        ))}
      </div>
    </section>
  )
}
